<template>
  <div>
    <PTransparentLoader :loading="loading">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1 class="main-header mb-0" v-translate translate-context="payout">
        Payout
      </h1>
      <div class="text-muted">
        <translate translate-context="payout">Contract / host guadagni e richieste i pagamenti</translate>
      </div>
    </div>
    
    <!-- Top section with two columns -->
    <CRow>
      <!-- Colonna sinistra con lista PicaService -->
      <CCol lg="5" xl="4" class="mb-4">
        <PicaServicesList
          v-if="hasAnyPerms(['payout.view_statistics'])"
        />
      </CCol>
      
      <!-- Colonna destra con summary cards -->
      <CCol lg="7" xl="8" class="mb-4">
        <!-- Summary Cards -->
        <PayoutSummaryCards
          v-if="hasAnyPerms(['payout.view_statistics']) && contract"
          :statistics="statistics"
          :currency="contract ? contract.currency : 'EUR'"
          :show-payout-button="hasAnyPerms(['payout.create_payout'])"
          @request-payout="openPayoutModal"
        />
      </CCol>
    </CRow>
    
    <!-- Full width table section -->
    <CRow>
      <CCol lg="12">
        <!-- Payout History Table -->
        <div class="mt-2">
          <PayoutHistoryTable
            :payouts="payouts"
            :currency="contract ? contract.currency : 'EUR'"
            @view-details="viewPayoutDetails"
            @complete-payout="openPayoutModalForRequested"
            @payout-deleted="onPayoutDeleted"
          />
          
          <!-- Pagination -->
          <CCard v-if="payouts.count > 0" class="mt-3">
            <CCardBody>
              <CRow>
                <CCol lg="6">
                  <div class="d-flex align-items-center">
                    <label class="mb-0 mr-2" v-translate translate-context="payout.pagination">Items per page:</label>
                    <CSelect
                      v-model="pagination.page_size"
                      @change="onPageSizeChange"
                      style="width: 100px;"
                    >
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </CSelect>
                    <span class="ml-3 text-muted">
                      <translate translate-context="payout.pagination" 
                        :translate-params="{from: ((pagination.page - 1) * pagination.page_size) + 1, to: Math.min(pagination.page * pagination.page_size, payouts.count), total: payouts.count}">
                        Showing %{from} to %{to} of %{total} items
                      </translate>
                    </span>
                  </div>
                </CCol>
                <CCol lg="6">
                  <CPagination
                    v-if="totalPages > 1"
                    :activePage.sync="pagination.page"
                    :pages="totalPages"
                    @update:activePage="onPageChange"
                    align="end"
                  />
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </div>
      </CCol>
    </CRow>
    
    <!-- Modal richiesta payout -->
    <PayoutRequestModal 
      v-if="hasAnyPerms(['payout.create_payout'])"
      ref="payoutModal"
      :contract="contract"
      :statistics="statistics"
      @success="onPayoutRequested"
    />
    </PTransparentLoader>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import PayoutRequestModal from './components/PayoutRequestModal.vue'
import PayoutSummaryCards from './components/PayoutSummaryCards.vue'
import PayoutHistoryTable from './components/PayoutHistoryTable.vue'
import PicaServicesList from './components/PicaServicesList.vue'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import eventBus from "@/domain/core/constant/eventBus";

export default {
  name: 'PayoutList',
  components: {
    PayoutRequestModal,
    PayoutSummaryCards,
    PayoutHistoryTable,
    PicaServicesList,
    PTransparentLoader
  },
  data() {
    return {
      loading: true,
      filters: {
        status: '',
        search: '',
        created_at_from: '',
        created_at_to: ''
      },
      pagination: {
        page: 1,
        page_size: 20
      },
      sorting: {
        column: 'created_at',
        asc: false
      },
      searchTimeout: null
    }
  },
  computed: {
    ...mapGetters('payout', ['payouts', 'statistics']),
    ...mapGetters('events', ['contract']),
    contractId() {
      return parseInt(this.$route.params.contractId)
    },
    totalPages() {
      return Math.ceil(this.payouts.count / this.pagination.page_size)
    }
  },
  methods: {
    ...mapActions('payout', [
      'fetchPayoutList', 
      'fetchPayoutStatistics', 
      'createPayout', 
      'requestPayout', 
      'deletePayout',
      'setContract'
    ]),
    ...mapActions('events', ['setActiveContract']),
    
    async loadPayoutData() {
      this.loading = true
      
      try {
        // Set contract ID from route
        if (!this.contractId) {
          await this.$router.push({name: 'eventList'})
          return
        }
        
        // Load contract data and permissions FIRST
        await this.setActiveContract(this.contractId)
        
        // Set contract in payout store AFTER contract is loaded
        await this.setContract(this.contractId)
        
        // Now that permissions are loaded, fetch statistics
        if (this.hasAnyPerms(['payout.view_statistics'])) {
          await this.fetchPayoutStatistics()
        }
        
        // Load the payout list
        await this.fetchList()
        
      } catch (error) {
        await this.notifyError(this.$pgettext('payout', 'Error loading payout data'))
        await this.$router.push({name: 'eventList'})
      } finally {
        this.loading = false
      }
    },
    
    async onChangeContract() {
      // When contract changes from eventBus, navigate to new URL
      if (this.contract && this.contract.id && this.contract.id !== this.contractId) {
        this.$router.push({
          name: 'payoutList',
          params: { contractId: this.contract.id }
        })
      }
    },
    
    async openPayoutModal() {
      // Step 1: Crea nuovo payout aggregando tutti gli ordini non pagati
      try {
        // Don't set loading to avoid re-render that might destroy the modal ref
        const createdPayout = await this.createPayout()
        
        // Aggiorna statistiche e lista dopo aver creato il payout
        await Promise.all([
          this.fetchPayoutStatistics(),
          this.fetchList()
        ])
        
        // Step 2: Find the newly created payout in the list (should be the first one with status 'pending')
        // If createPayout doesn't return the full payout object, get it from the list
        let payoutToOpen = createdPayout
        if (!createdPayout || !createdPayout.id) {
          // Find the most recent pending payout from the list
          payoutToOpen = this.payouts.results.find(p => p.status === 'pending')
        }
        
        // Step 3: Apri modale con dati del payout creato - use setTimeout instead of nextTick to ensure DOM is stable
        setTimeout(() => {
          if (this.$refs.payoutModal) {
            this.$refs.payoutModal.open(payoutToOpen)
          }
        }, 100)
      } catch (error) {
        this.notifyError(this.$pgettext('payout', 'Error creating payout'))
      }
    },
    
    openPayoutModalForRequested(payout) {
      // Per payout in stato pending che necessitano di fattura e IBAN
      if (this.$refs.payoutModal) {
        this.$refs.payoutModal.open(payout)
      }
    },
    
    async fetchList() {
      // Prepare filters object, only include non-empty values
      const activeFilters = {}
      if (this.filters.status) activeFilters.status = this.filters.status
      if (this.filters.search) activeFilters.search = this.filters.search
      if (this.filters.created_at_from) {
        activeFilters.created_at = `${this.filters.created_at_from}T00:00:00Z`
      }
      if (this.filters.created_at_to) {
        activeFilters.created_at_to = `${this.filters.created_at_to}T23:59:59Z`
      }
      
      await this.fetchPayoutList({
        filters: activeFilters,
        pagination: this.pagination,
        sorting: this.sorting
      })
    },
    
    applyFilters() {
      this.pagination.page = 1 // Reset to first page when filters change
      this.fetchList()
    },
    
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.applyFilters()
      }, 500)
    },
    
    onSort(column) {
      if (this.sorting.column === column) {
        this.sorting.asc = !this.sorting.asc
      } else {
        this.sorting.column = column
        this.sorting.asc = true
      }
      this.fetchList()
    },
    
    onPageChange(page) {
      this.pagination.page = page
      this.fetchList()
    },
    
    onPageSizeChange() {
      this.pagination.page = 1 // Reset to first page when page size changes
      this.fetchList()
    },
    
    async onPayoutRequested() {
      if (this.hasAnyPerms(['payout.view_statistics'])) {
        await this.fetchPayoutStatistics()
      }
      await this.fetchList()
      this.notifySuccess(this.$pgettext('payout', 'Payout request sent successfully'))
    },
    
    async onPayoutDeleted() {
      if (this.hasAnyPerms(['payout.view_statistics'])) {
        await this.fetchPayoutStatistics()
      }
      await this.fetchList()
    },
    
    viewPayoutDetails(payout) {
      this.$router.push({
        name: 'payoutDetail',
        params: { 
          contractId: this.contractId,
          payoutId: payout.id 
        }
      })
    }
  },
  async mounted() {
    this.registerEvent(eventBus.contract.changed, this.onChangeContract)
    await this.loadPayoutData()
  },
  beforeDestroy() {
    this.unregisterEvent(eventBus.contract.changed, this.onChangeContract)
  }
}
</script>

<style scoped>
/* Custom styles for better layout */
@media (min-width: 992px) {
  .col-lg-5 {
    position: sticky;
    top: 20px;
    align-self: flex-start;
  }
}
</style>